// src/components/ErrorBoundary.jsx
// ─────────────────────────────────────────────────────────────
// Global Error Boundary - Catches React errors and reports to backend
// ─────────────────────────────────────────────────────────────

import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('🔴 ErrorBoundary caught:', error, errorInfo);

        this.setState({
            error,
            errorInfo
        });

        // Send error to backend
        this.reportErrorToBackend(error, errorInfo);
    }

    reportErrorToBackend = async (error, errorInfo) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return; // Don't report if not authenticated

            const errorPayload = {
                error_type: error.name || 'UnknownError',
                error_message: error.message || 'No error message',
                stack_trace: errorInfo.componentStack || error.stack || 'No stack trace',
                component: errorInfo.componentStack?.split('\n')[1]?.trim() || 'Unknown',
                url: window.location.href,
                user_agent: navigator.userAgent
            };

            await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/monitor/client-error`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(errorPayload)
            });

            console.log('✅ Error reported to backend');
        } catch (reportError) {
            console.error('❌ Failed to report error to backend:', reportError);
        }
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'background.default',
                        p: 3
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            maxWidth: 600,
                            p: 4,
                            textAlign: 'center',
                            borderTop: '4px solid #FF003C'
                        }}
                    >
                        <ErrorOutlineIcon sx={{ fontSize: 80, color: '#FF003C', mb: 2 }} />

                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                            Oops! Algo deu errado
                        </Typography>

                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                            Encontramos um erro inesperado. Nossa equipe foi notificada automaticamente.
                        </Typography>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Paper
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    bgcolor: 'rgba(255, 0, 60, 0.05)',
                                    border: '1px solid rgba(255, 0, 60, 0.2)',
                                    textAlign: 'left'
                                }}
                            >
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#FF003C', display: 'block', mb: 1 }}>
                                    <strong>Error:</strong> {this.state.error.toString()}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', whiteSpace: 'pre-wrap' }}>
                                    {this.state.errorInfo?.componentStack}
                                </Typography>
                            </Paper>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={this.handleReload}
                            sx={{
                                bgcolor: '#FF003C',
                                color: '#FFF',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                px: 4,
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: '#C70030'
                                }
                            }}
                        >
                            Recarregar Página
                        </Button>
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
